import React, { useState, useEffect } from 'react';
import { Settings, Lock, BookOpen, Sliders, Trophy, LogOut, Download, Upload, Trash2, AlertCircle, Wrench } from 'lucide-react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { collection, getDocs, deleteDoc, doc, writeBatch, query, orderBy, setDoc, getDoc, limit } from 'firebase/firestore';
import * as XLSX from 'xlsx';

function AdminPanel({ onClose, gameConfig, onConfigUpdate, onLoginStateChange, onQuestionsUpdated }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [uploadMode, setUploadMode] = useState('replace');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardFilter, setLeaderboardFilter] = useState('all');
  const [stats, setStats] = useState({
    totalGames: 0, totalQuestions: 0, avgScoreAll: 0, avgScoreVocab: 0, avgScoreGrammar: 0,
    topScoreAll: 0, topScoreVocab: 0, topScoreGrammar: 0, gamesVocab: 0, gamesGrammar: 0, recentGames: []
  });
  const [editedConfig, setEditedConfig] = useState(gameConfig);
  const [configSaveStatus, setConfigSaveStatus] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsAuthenticated(true);
        if (onLoginStateChange) onLoginStateChange(true);
      }
    });
    return () => unsubscribe();
  }, [onLoginStateChange]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setIsAuthenticated(true);
      if (onLoginStateChange) onLoginStateChange(true);
    } catch (error) {
      if (error.code === 'auth/invalid-credential') setLoginError('Invalid email or password');
      else if (error.code === 'auth/too-many-requests') setLoginError('Too many failed attempts. Try again later.');
      else setLoginError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAuthenticated(false);
      if (onLoginStateChange) onLoginStateChange(false);
      setActiveTab(null);
      setEmail('');
      setPassword('');
      onClose();
    } catch (error) {}
  };

  const loadQuestions = async () => {
    try {
      const questionsSnapshot = await getDocs(collection(db, 'questions'));
      const loadedQuestions = [];
      questionsSnapshot.forEach((doc) => loadedQuestions.push({ id: doc.id, ...doc.data() }));
      setQuestions(loadedQuestions);
    } catch (error) {
      setUploadStatus('Error loading questions');
    }
  };

  const downloadQuestionsXLSX = () => {
    const wsData = [
      ['Category', 'Question', 'Answer 1 (correct)', 'Answer 2', 'Answer 3', 'Answer 4', 'Description'],
      ...questions.map(q => [q.category || '', q.question || '', q.answers[0] || '', q.answers[1] || '', q.answers[2] || '', q.answers[3] || '', q.description || ''])
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 10 }, { wch: 60 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Questions');
    XLSX.writeFile(wb, `quiz_questions_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const validateFile = (file) => {
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) return { valid: true, type: 'xlsx' };
    if (fileName.endsWith('.csv')) return { valid: true, type: 'csv' };
    return { valid: false, error: 'Please upload an Excel (.xlsx) or CSV (.csv) file' };
  };

  const parseXLSX = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const parsedQuestions = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length < 6) continue;
            const question = {
              category: String(row[0] || '').toLowerCase().trim(),
              question: String(row[1] || '').trim(),
              answers: [String(row[2] || '').trim(), String(row[3] || '').trim(), String(row[4] || '').trim(), String(row[5] || '').trim()],
              correct: 0,
              description: String(row[6] || '').trim()
            };
            if (question.question && question.answers.every(a => a)) parsedQuestions.push(question);
          }
          resolve(parsedQuestions);
        } catch (error) { reject(error); }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const validateCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return { valid: false, error: 'File is empty or has no questions' };
    const headerLine = lines[0];
    const hasCategory = headerLine.toLowerCase().includes('category');
    const hasQuestion = headerLine.toLowerCase().includes('question');
    const hasAnswers = headerLine.toLowerCase().includes('answer');
    if (!hasCategory || !hasQuestion || !hasAnswers) {
      return { valid: false, error: `Header must contain: Category, Question, Answer 1 (correct), Answer 2, Answer 3, Answer 4, Description\nGot: ${headerLine}` };
    }
    return { valid: true, validQuestions: lines.length - 1 };
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const parsedQuestions = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const fields = [];
      let currentField = '';
      let insideQuotes = false;
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          if (insideQuotes && j + 1 < line.length && line[j + 1] === '"') { currentField += '"'; j++; }
          else insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) { fields.push(currentField.trim()); currentField = ''; }
        else if (char !== '\r') currentField += char;
      }
      fields.push(currentField.trim());
      if (fields.length < 6) continue;
      const question = { category: fields[0].toLowerCase(), question: fields[1], answers: [fields[2], fields[3], fields[4], fields[5]], correct: 0, description: fields[6] || '' };
      if (question.question && question.answers.every(a => a)) parsedQuestions.push(question);
    }
    return parsedQuestions;
  };

  const handleFileUpload = async () => {
    if (!uploadFile) { setUploadStatus('Please select a file'); return; }
    setLoading(true);
    setUploadStatus('Processing file...');
    try {
      const fileValidation = validateFile(uploadFile);
      if (!fileValidation.valid) { setUploadStatus(`❌ ${fileValidation.error}`); setLoading(false); return; }
      let newQuestions = [];
      if (fileValidation.type === 'xlsx') {
        setUploadStatus('📊 Reading Excel file...');
        newQuestions = await parseXLSX(uploadFile);
      } else {
        setUploadStatus('📄 Reading CSV file...');
        const text = await uploadFile.text();
        const validation = validateCSV(text);
        if (!validation.valid) { setUploadStatus(`❌ Validation failed:\n${validation.error}`); setLoading(false); return; }
        newQuestions = parseCSV(text);
      }
      if (newQuestions.length === 0) { setUploadStatus('❌ No valid questions found in file'); setLoading(false); return; }
      setUploadStatus(`✅ Found ${newQuestions.length} questions. Uploading to database...`);
      const batch = writeBatch(db);
      if (uploadMode === 'replace') {
        const existingQuestions = await getDocs(collection(db, 'questions'));
        existingQuestions.forEach((doc) => batch.delete(doc.ref));
      }
      newQuestions.forEach((question) => { const newDocRef = doc(collection(db, 'questions')); batch.set(newDocRef, question); });
      await batch.commit();
      setUploadStatus(`✅ Successfully ${uploadMode === 'replace' ? 'replaced' : 'added'} ${newQuestions.length} questions!`);
      setUploadFile(null);
      await loadQuestions();
      if (onQuestionsUpdated) await onQuestionsUpdated();
      setTimeout(() => setUploadStatus(''), 3000);
    } catch (error) {
      setUploadStatus(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const leaderboardQuery = query(collection(db, 'leaderboard'), orderBy('score', 'desc'));
      const leaderboardSnapshot = await getDocs(leaderboardQuery);
      const loadedLeaderboard = [];
      leaderboardSnapshot.forEach((doc) => loadedLeaderboard.push({ id: doc.id, ...doc.data() }));
      setLeaderboardData(loadedLeaderboard);
    } catch (error) {}
  };

  const deleteLeaderboardEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await deleteDoc(doc(db, 'leaderboard', id));
      await loadLeaderboard();
    } catch (error) {
      alert('Error deleting entry');
    }
  };

  const resetLeaderboard = async () => {
    if (!window.confirm('⚠️ This will DELETE ALL leaderboard entries. Are you sure?')) return;
    if (!window.confirm('This cannot be undone. Proceed?')) return;
    try {
      const batch = writeBatch(db);
      const leaderboardSnapshot = await getDocs(collection(db, 'leaderboard'));
      leaderboardSnapshot.forEach((doc) => batch.delete(doc.ref));
      await batch.commit();
      setLeaderboardData([]);
      alert('✅ Leaderboard reset successfully');
    } catch (error) {
      alert('❌ Error resetting leaderboard');
    }
  };

  const filteredLeaderboard = leaderboardFilter === 'all' ? leaderboardData : leaderboardData.filter(entry => entry.category === leaderboardFilter);

  const calculateStats = async () => {
    try {
      const leaderboardQuery = query(collection(db, 'leaderboard'), orderBy('timestamp', 'desc'));
      const leaderboardSnapshot = await getDocs(leaderboardQuery);
      const allScores = [], vocabScores = [], grammarScores = [], recentGames = [];
      leaderboardSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.score > 0) allScores.push(data.score);
        if (data.category === 'vocab') vocabScores.push(data.score);
        else if (data.category === 'grammar') grammarScores.push(data.score);
        if (recentGames.length < 10) recentGames.push({ name: data.name, score: data.score, category: data.category, date: data.date });
      });
      const questionsSnapshot = await getDocs(collection(db, 'questions'));
      setStats({
        totalGames: allScores.length,
        totalQuestions: questionsSnapshot.size,
        avgScoreAll: allScores.length > 0 ? Math.round(allScores.reduce((a, b) => a + b, 0) / allScores.length) : 0,
        avgScoreVocab: vocabScores.length > 0 ? Math.round(vocabScores.reduce((a, b) => a + b, 0) / vocabScores.length) : 0,
        avgScoreGrammar: grammarScores.length > 0 ? Math.round(grammarScores.reduce((a, b) => a + b, 0) / grammarScores.length) : 0,
        topScoreAll: allScores.length > 0 ? Math.max(...allScores) : 0,
        topScoreVocab: vocabScores.length > 0 ? Math.max(...vocabScores) : 0,
        topScoreGrammar: grammarScores.length > 0 ? Math.max(...grammarScores) : 0,
        gamesVocab: vocabScores.length,
        gamesGrammar: grammarScores.length,
        recentGames
      });
    } catch (error) {}
  };

  const saveGameConfig = async () => {
    setLoading(true);
    setConfigSaveStatus('Saving...');
    try {
      await setDoc(doc(db, 'settings', 'config'), editedConfig);
      onConfigUpdate(editedConfig);
      setConfigSaveStatus('✅ Settings saved successfully!');
      setTimeout(() => setConfigSaveStatus(''), 3000);
    } catch (error) {
      setConfigSaveStatus('❌ Error saving settings');
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset all settings to default values?')) return;
    setEditedConfig({
      BASE_TIMER: 20, TIME_PER_CORRECT: 4, TIME_PENALTY_WRONG: 3, TIMER_CAP: 20, DRAIN_ACCELERATION: 1.15, ACCELERATION_INTERVAL: 20,
      POINTS_BASE: 100, SPEED_BONUS_FAST: 50, SPEED_BONUS_MEDIUM: 30, SPEED_BONUS_SLOW: 10,
      STREAK_5_MULTIPLIER: 1.2, STREAK_10_MULTIPLIER: 1.5, STREAK_15_MULTIPLIER: 2.0,
      STREAK_5_BONUS: 2, STREAK_10_BONUS: 4, STREAK_15_BONUS: 6, 
      ALLOW_RETRY_AFTER_WRONG: true, WRONG_ANSWER_DELAY: 1.0, MAX_NAME_LENGTH: 20
    });
  };

  useEffect(() => {
    if (activeTab === 'questions') loadQuestions();
    else if (activeTab === 'leaderboard') loadLeaderboard();
    else if (activeTab === 'config') calculateStats();
    else if (activeTab === 'settings') setEditedConfig(gameConfig);
  }, [activeTab, gameConfig]);

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Lock className="text-purple-600" size={32} />
              <h2 className="text-3xl font-bold text-gray-800">Admin Login</h2>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="admin@example.com" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
                placeholder="••••••••" />
            </div>
            {loginError && <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-xl">{loginError}</div>}
            <button type="submit" disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-xl transition disabled:opacity-50">
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!activeTab) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Settings className="text-purple-600" size={32} />
              <h2 className="text-3xl font-bold text-gray-800">Admin Panel</h2>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition">
              <LogOut size={20} />Logout
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button onClick={() => setActiveTab('questions')}
              className="bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white p-6 rounded-xl transition transform hover:scale-105 shadow-lg">
              <BookOpen size={48} className="mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Question Bank</h3>
              <p className="text-sm opacity-90">Upload/download questions</p>
            </button>
            <button onClick={() => setActiveTab('leaderboard')}
              className="bg-gradient-to-br from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white p-6 rounded-xl transition transform hover:scale-105 shadow-lg">
              <Trophy size={48} className="mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Leaderboard</h3>
              <p className="text-sm opacity-90">Manage scores and names</p>
            </button>
            <button onClick={() => setActiveTab('config')}
              className="bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white p-6 rounded-xl transition transform hover:scale-105 shadow-lg">
              <Sliders size={48} className="mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Game Stats</h3>
              <p className="text-sm opacity-90">View game statistics</p>
            </button>
            <button onClick={() => setActiveTab('settings')}
              className="bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white p-6 rounded-xl transition transform hover:scale-105 shadow-lg">
              <Wrench size={48} className="mx-auto mb-3" />
              <h3 className="text-xl font-bold mb-2">Game Settings</h3>
              <p className="text-sm opacity-90">Edit timers, scoring, streaks</p>
            </button>
          </div>
          <button onClick={onClose} className="w-full mt-6 bg-gray-400 hover:bg-gray-500 text-white font-bold py-3 px-6 rounded-xl transition">
            Close Admin Panel
          </button>
        </div>
      </div>
    );
  }

  if (activeTab === 'questions') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-800">Question Bank Manager</h2>
            <button onClick={() => setActiveTab(null)} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition">← Back</button>
          </div>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2"><Download size={24} />Download Questions</h3>
            <p className="text-gray-600 mb-4">Download all {questions.length} questions as an Excel file. Edit in Excel/Google Sheets and re-upload.</p>
            <button onClick={downloadQuestionsXLSX} disabled={questions.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition flex items-center gap-2">
              <Download size={20} />Download Excel ({questions.length} questions)
            </button>
          </div>
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2"><Upload size={24} />Upload Questions</h3>
            <p className="text-gray-600 mb-4"><strong>📊 Recommended:</strong> Upload Excel (.xlsx) files - no comma issues!<br/>Also supports CSV files with proper formatting.</p>
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">Upload Mode:</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" value="replace" checked={uploadMode === 'replace'} onChange={(e) => setUploadMode(e.target.value)} className="w-4 h-4" />
                  <span>Replace all existing questions ⚠️</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" value="append" checked={uploadMode === 'append'} onChange={(e) => setUploadMode(e.target.value)} className="w-4 h-4" />
                  <span>Add to existing questions ✅</span>
                </label>
              </div>
            </div>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setUploadFile(e.target.files[0])}
              className="mb-4 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200" />
            {uploadStatus && (
              <div className={`mb-4 p-3 rounded-lg whitespace-pre-line ${uploadStatus.includes('✅') ? 'bg-green-100 text-green-700' : uploadStatus.includes('❌') ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                {uploadStatus}
              </div>
            )}
            <button onClick={handleFileUpload} disabled={!uploadFile || loading}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              <Upload size={20} />{loading ? 'Uploading...' : 'Upload File'}
            </button>
            {uploadMode === 'replace' && (
              <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700"><strong>Warning:</strong> Replace mode will delete all {questions.length} existing questions before uploading new ones.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === 'leaderboard') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-800">Leaderboard Manager</h2>
            <button onClick={() => setActiveTab(null)} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition">← Back</button>
          </div>
          <div className="flex gap-2 mb-6">
            {[{ key: 'all', label: 'All' }, { key: 'vocab', label: 'Vocabulary' }, { key: 'grammar', label: 'Grammar' }, { key: 'spelling', label: 'Spelling' }].map(({ key, label }) => (
              <button key={key} onClick={() => setLeaderboardFilter(key)}
                className={`px-4 py-2 rounded-lg font-bold transition ${leaderboardFilter === key ? (key === 'grammar' ? 'bg-blue-600' : key === 'spelling' ? 'bg-green-600' : 'bg-purple-600') + ' text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                {label} ({key === 'all' ? leaderboardData.length : leaderboardData.filter(e => e.category === key).length})
              </button>
            ))}
          </div>
          <div className="mb-6">
            <button onClick={resetLeaderboard} className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition flex items-center gap-2">
              <Trash2 size={20} />Reset Entire Leaderboard
            </button>
          </div>
          {filteredLeaderboard.length === 0 ? (
            <div className="text-center py-12 text-gray-500"><Trophy size={48} className="mx-auto mb-4 opacity-30" /><p className="text-xl">No entries found</p></div>
          ) : (
            <div className="space-y-2">
              {filteredLeaderboard.map((entry, index) => (
                <div key={entry.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition">
                  <div className="flex items-center gap-4">
                    <span className="text-xl font-bold text-gray-700 w-8">{index + 1}</span>
                    <div>
                      <div className="font-bold text-lg">{entry.name}</div>
                      <div className="text-sm text-gray-500">{entry.category} • Score: {entry.score}</div>
                    </div>
                  </div>
                  <button onClick={() => deleteLeaderboardEntry(entry.id)} className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition" title="Delete entry">
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (activeTab === 'config') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-800">Game Statistics</h2>
            <button onClick={() => setActiveTab(null)} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition">← Back</button>
          </div>
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Overall Performance</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <div className="text-3xl font-bold text-blue-600 mb-1">{stats.totalGames}</div>
                <div className="text-sm text-gray-600">Total Games</div>
              </div>
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
                <div className="text-3xl font-bold text-purple-600 mb-1">{stats.totalQuestions}</div>
                <div className="text-sm text-gray-600">Total Questions</div>
              </div>
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                <div className="text-3xl font-bold text-green-600 mb-1">{stats.avgScoreAll}</div>
                <div className="text-sm text-gray-600">Avg Score (All)</div>
              </div>
            </div>
          </div>
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Category Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                <h4 className="text-lg font-bold text-purple-800 mb-4">📚 Vocabulary</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center"><span className="text-gray-600">Games Played:</span><span className="font-bold text-lg">{stats.gamesVocab}</span></div>
                  <div className="flex justify-between items-center"><span className="text-gray-600">Average Score:</span><span className="font-bold text-lg text-purple-600">{stats.avgScoreVocab}</span></div>
                  <div className="flex justify-between items-center"><span className="text-gray-600">Top Score:</span><span className="font-bold text-lg text-purple-600">{stats.topScoreVocab}</span></div>
                </div>
              </div>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                <h4 className="text-lg font-bold text-blue-800 mb-4">📝 Grammar</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center"><span className="text-gray-600">Games Played:</span><span className="font-bold text-lg">{stats.gamesGrammar}</span></div>
                  <div className="flex justify-between items-center"><span className="text-gray-600">Average Score:</span><span className="font-bold text-lg text-blue-600">{stats.avgScoreGrammar}</span></div>
                  <div className="flex justify-between items-center"><span className="text-gray-600">Top Score:</span><span className="font-bold text-lg text-blue-600">{stats.topScoreGrammar}</span></div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Recent Activity (Last 10 Games)</h3>
            {stats.recentGames.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No games played yet</p>
            ) : (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
                <div className="space-y-2">
                  {stats.recentGames.map((game, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-bold text-gray-500 w-6">{index + 1}</span>
                        <div>
                          <div className="font-bold">{game.name}</div>
                          <div className="text-xs text-gray-500">{game.category} • {new Date(game.date).toLocaleString('en-SG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      </div>
                      <div className="text-lg font-bold text-purple-600">{game.score}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (activeTab === 'settings') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-800">Game Settings</h2>
            <div className="flex gap-2">
              <button onClick={resetToDefaults} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-lg transition">Reset to Defaults</button>
              <button onClick={() => setActiveTab(null)} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition">← Back</button>
            </div>
          </div>
          {configSaveStatus && (
            <div className={`mb-4 p-3 rounded-lg ${configSaveStatus.includes('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{configSaveStatus}</div>
          )}
          <div className="space-y-6">
            {/* Timer Settings */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">⏱️ Timer Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'Starting Time (seconds)', key: 'BASE_TIMER' },
                  { label: 'Time Added (correct answer)', key: 'TIME_PER_CORRECT' },
                  { label: 'Time Lost (wrong answer)', key: 'TIME_PENALTY_WRONG' },
                  { label: 'Timer Cap (max seconds)', key: 'TIMER_CAP' },
                  { label: 'Drain Acceleration (multiplier)', key: 'DRAIN_ACCELERATION', step: '0.01', parse: parseFloat },
                  { label: 'Acceleration Interval (seconds)', key: 'ACCELERATION_INTERVAL' }
                ].map(({ label, key, step, parse = parseInt }) => (
                  <div key={key}>
                    <label className="block text-sm font-bold text-gray-700 mb-2">{label}</label>
                    <input type="number" step={step} value={editedConfig[key]}
                      onChange={(e) => setEditedConfig({...editedConfig, [key]: parse(e.target.value)})}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>
            {/* Scoring Settings */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">🎯 Scoring Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'Base Points', key: 'POINTS_BASE' },
                  { label: 'Speed Bonus: Fast (<3s)', key: 'SPEED_BONUS_FAST' },
                  { label: 'Speed Bonus: Medium (3-5s)', key: 'SPEED_BONUS_MEDIUM' },
                  { label: 'Speed Bonus: Slow (5-8s)', key: 'SPEED_BONUS_SLOW' }
                ].map(({ label, key }) => (
                  <div key={key}>
                    <label className="block text-sm font-bold text-gray-700 mb-2">{label}</label>
                    <input type="number" value={editedConfig[key]}
                      onChange={(e) => setEditedConfig({...editedConfig, [key]: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>
            {/* Streak Settings */}
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">🔥 Streak Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: '5-Streak Multiplier', key: 'STREAK_5_MULTIPLIER', step: '0.1', parse: parseFloat },
                  { label: '5-Streak Time Bonus (s)', key: 'STREAK_5_BONUS' },
                  { label: '10-Streak Multiplier', key: 'STREAK_10_MULTIPLIER', step: '0.1', parse: parseFloat },
                  { label: '10-Streak Time Bonus (s)', key: 'STREAK_10_BONUS' },
                  { label: '15-Streak Multiplier', key: 'STREAK_15_MULTIPLIER', step: '0.1', parse: parseFloat },
                  { label: '15-Streak Time Bonus (s)', key: 'STREAK_15_BONUS' }
                ].map(({ label, key, step, parse = parseInt }) => (
                  <div key={key}>
                    <label className="block text-sm font-bold text-gray-700 mb-2">{label}</label>
                    <input type="number" step={step} value={editedConfig[key]}
                      onChange={(e) => setEditedConfig({...editedConfig, [key]: parse(e.target.value)})}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>
            {/* Gameplay Settings */}
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">🎮 Gameplay Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editedConfig.ALLOW_RETRY_AFTER_WRONG}
                      onChange={(e) => setEditedConfig({...editedConfig, ALLOW_RETRY_AFTER_WRONG: e.target.checked})} className="w-5 h-5" />
                    <span className="font-bold text-gray-700">Allow Retry After Wrong Answer</span>
                  </label>
                  <p className="text-sm text-gray-500 mt-1 ml-7">Students can keep trying until they get the correct answer</p>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Max Name Length</label>
                  <input type="number" value={editedConfig.MAX_NAME_LENGTH}
                    onChange={(e) => setEditedConfig({...editedConfig, MAX_NAME_LENGTH: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Wrong Answer Feedback Delay (seconds)</label>
                  <input type="number" step="0.1" min="0" max="5" value={editedConfig.WRONG_ANSWER_DELAY || 1.0}
                    onChange={(e) => setEditedConfig({...editedConfig, WRONG_ANSWER_DELAY: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none" />
                  <p className="text-sm text-gray-500 mt-1">Animation to show wrong answer (minimum 0.5 seconds)</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={saveGameConfig} disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition disabled:opacity-50 text-lg">
              {loading ? 'Saving...' : '💾 Save All Settings'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default AdminPanel;